import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'store';
import { isEventManager } from 'utils/helpers';
import { Role, Roles, BackOfficeRoles } from 'types';
import { salesRoles, getAdvanceAccess } from 'config';

import { types } from './types';
import { login, logout } from './thunks';

const authStore = (store: RootState) => store?.auth;

const selectIsLoggedIn = createSelector(
  [authStore],
  (store) => store?.isLoggedIn ?? false
);

const selectTokenUpdated = createSelector(
  [authStore],
  (store) => store?.isTokenUpdated ?? false
);

const selectAuthError = createSelector([authStore], (store) => store?.error);

const selectAuthRestorePath = createSelector(
  [authStore],
  (store) => store?.path
);

const selectAuthUser = createSelector([authStore], (store) => store?.user);

const selectAuthUserFeatures = createSelector(
  [authStore],
  (store) => store?.user?.features
);

const selectAuthUserRole = createSelector(
  [authStore],
  (store) => store?.user?.role?.name
);

const isAuthCompanyUser = createSelector(
  [authStore],
  (store) => store?.user?.role?.name === Roles.COMPANY_OWNER
);

const selectAuthUserType = createSelector(
  [authStore],
  (store) => store?.user?.userType
);

const isEventManagerRole = createSelector([authStore], (store) =>
  isEventManager(store?.user)
);

const isCompanyOwnerOrSiteAdmin = createSelector([authStore], (store) =>
  [Roles.SITE_ADMIN, Roles.COMPANY_OWNER].includes(
    store?.user?.role?.name as Roles
  )
);

const selectCanReadMissingSales = createSelector([selectAuthUserRole], (role) =>
  [
    Roles.COMPANY_OWNER,
    BackOfficeRoles.ACCOUNTING,
    BackOfficeRoles.ADMINISTRATIVE,
  ].includes(role as BackOfficeRoles | Roles.COMPANY_OWNER)
);

// Owner 18 Oct 2026: Save PDF on the event report (summary and daily) only
// for these roles; everyone else keeps the on-screen report. The backend
// returns 403 on /report-pdf for the rest.
const selectCanExportEventReport = createSelector(
  [selectAuthUserRole],
  (role) =>
    [
      Roles.SITE_ADMIN,
      Roles.COMPANY_OWNER,
      BackOfficeRoles.ACCOUNTING,
      BackOfficeRoles.ADMINISTRATIVE,
    ].includes(role as Roles | BackOfficeRoles)
);

const selectAdvanceAccess = createSelector([selectAuthUserRole], (role) =>
  getAdvanceAccess(role)
);

const isSaleRole = createSelector([authStore], (store) =>
  salesRoles.includes(store?.user?.role?.name as BackOfficeRoles)
);

export const hasUserRole = (requiredRole: Role | Role[]) =>
  createSelector([selectAuthUserRole], (userRole) =>
    Array.isArray(requiredRole)
      ? requiredRole.includes(userRole as Role)
      : userRole === requiredRole
  );

export {
  login,
  types,
  logout,
  isSaleRole,
  selectAuthUser,
  selectAuthError,
  selectIsLoggedIn,
  isAuthCompanyUser,
  selectTokenUpdated,
  isEventManagerRole,
  selectAuthUserRole,
  selectAuthUserType,
  selectAdvanceAccess,
  selectAuthRestorePath,
  selectAuthUserFeatures,
  isCompanyOwnerOrSiteAdmin,
  selectCanReadMissingSales,
  selectCanExportEventReport,
};
